import type { ReactElement } from 'react';
import { useState } from 'react';

import { ApiRoute } from 'config';
import { post } from 'utils/network';
import { getQueryParam } from 'utils/location';
import { toCurrency } from 'utils/numbers';

import IApiResponse from 'models/IApiResponse';
import IAppliedCode from 'models/IAppliedCode';

import Banner from 'components/shared/Banner';
import ValidableControl from 'components/shared/ValidableControl';
import Button from 'react-bootstrap/Button';
import useCurrentUser from '../../hooks/useCurrentUser';

type AppliedCode = {
  code: string;
  value: number | null;
  discount: string;
};

function RedeemGift(): ReactElement {
  // @ts-expect-error TODO: fix null safety
  const [appliedCode, setAppliedCode] = useState<AppliedCode | null>(() => {
    if (getQueryParam('success') !== 'true') return null;

    const usedCode = getQueryParam('code');
    // @ts-expect-error TODO: fix null safety
    const value = Number.parseFloat(getQueryParam('value'));
    const discountType = getQueryParam('discount_type');
    const couponDiscount = discountType === 'dollar_off' ? toCurrency(value) : getQueryParam('discount');
    return {
      code: usedCode,
      value: Number.isNaN(value) ? null : value,
      discount: couponDiscount,
    };
  });
  const [code, setCode] = useState(getQueryParam('code') || '');
  const [errorMessage, setErrorMessage] = useState(null);
  const { reload } = useCurrentUser();

  let content: ReactElement;

  const resetForm = () => {
    setCode('');
    setAppliedCode(null);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    const resp = await post(ApiRoute.REDEEM_CODE, { code });

    if (resp.status === 201) {
      const {
        discount_code: usedCode,
        discount_price: value,
        discount: couponDiscount,
        discount_type: couponDiscountType,
      } = resp.parsedBody as IAppliedCode;

      setAppliedCode({
        code: usedCode,
        // @ts-expect-error TODO: fix null safety
        value: (value === 0 ? null : value),
        // @ts-expect-error TODO: fix null safety
        discount: couponDiscountType === 'dollar_off'
        // @ts-expect-error TODO: fix null safety
          ? toCurrency(value)
          : couponDiscount,
      });

      reload();
    } else if (resp.status === 422) {
      const parsedBody = resp.parsedBody as IApiResponse;
      // @ts-expect-error TODO: fix null safety
      setErrorMessage(parsedBody.details || undefined);
    }
  };

  if (appliedCode === null) {
    content = (
      <form onSubmit={(e) => submitCode(e)}>
        <ValidableControl
          label="Enter your code"
          htmlFor="edit--code"
          isInvalid={errorMessage !== null}
          errorMessage={(
              // @ts-expect-error TODO: fix null safety
            <div dangerouslySetInnerHTML={{ __html: errorMessage }} />
          )}
        >
          <input
            id="edit--code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </ValidableControl>
        <Button
          type="submit"
          className="c-button c-button--primary"
          disabled={code.trim().length === 0}
        >
          Redeem Now
        </Button>
      </form>
    );
  } else {
    content = (
      <>
        <Banner.Success>
          The code
          <strong className="mx-1">{appliedCode.code.toUpperCase()}</strong>
          {appliedCode.discount.length !== 0 && (
            <>
              <span>with</span>
              <strong className="mx-1">{appliedCode.discount}</strong>
            </>
          )}
          will be applied to your account!
        </Banner.Success>
        <div>
          <button type="button" className="c-link" onClick={resetForm}>
            Enter another code
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="col-lg-10">
      <div className="c-redeem-a-gift">
        <div className="row justify-content-center">
          <div className="col-xs-8 col-sm-7">
            <div className="c-redeem-a-gift__title">
              <h2 className="c-heading c-heading--brand-4">
                Claim Your Gift, Loyalty Rewards, or Credits
              </h2>
              <p className="u-text--md u-text--lg-lg">
                You can redeem one code per order.
                Discount will be reflected on your next order.
                Other restrictions may apply.
              </p>
            </div>
            {content}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RedeemGift;
